import React, { useCallback } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { FlexBox, useNotificationEvents } from '@agilepoint/wat-react-ux';
import { useTranslate } from '@agilepoint/wat-react';
import { UploadImagesTab } from 'modules/shared/content';
import { useUploadImageDefaultValues } from 'modules/shared/hooks';
import { IUploadImagesFormFields } from 'modules/shared/types';
import { IBinaryDataPayload } from 'modules/shared/queries';
import { TabFooterButtons } from './tab-footer-buttons';
import { getMappedFormfieldName, getNativeFileName } from 'modules/shared/functions';
import { PlatformTypes } from 'modules/create-new-app/components/app-wizard/steps';

interface IUploadImagesProps {
    imageData: {
        name: string;
        imageUrl: string;
    }[];
    mutationLoading: boolean;
    onSaveBinaryData: (payloadArray: IBinaryDataPayload[]) => Promise<any>;
    menuColor?: string;
}

const UploadImagesBase = React.memo((props: IUploadImagesProps) => {
    const { mutationLoading, imageData: initialImageData, onSaveBinaryData, menuColor } = props;
    const { handleSubmit, formState } = useFormContext<IUploadImagesFormFields>();
    const { showNotifications } = useNotificationEvents();
    const translate = useTranslate();
    const headerLabel = translate('modules.createNewApp.steps.uploadImages.defaultCustomImagesHeader');

    const onSubmitHandler = useCallback((formData: IUploadImagesFormFields) => {
        const previouslyUploadedImageFormFields = initialImageData.reduce((acc, currentImageData) => {
            if (formData[getMappedFormfieldName(currentImageData.name)] === currentImageData.imageUrl) {
                acc.push(getMappedFormfieldName(currentImageData.name));
            }
            return acc;
        }, [] as string[]);

        const dirtyFormFields = Object.keys(formState.dirtyFields);
        const binaryDataPayload = [...previouslyUploadedImageFormFields, ...dirtyFormFields].map((field) => ({
            AppId: 'Global',
            BinaryDataAsBase64: formData[field].split(',')[1],
            ContentType: 'png',
            FileName: getNativeFileName(field)
        }));
        onSaveBinaryData(binaryDataPayload)
            .then(() => showNotifications({ message: translate('modules.createNewApp.steps.uploadImages.successfulImageUpload'), type: 'success' }))
            .catch(() => showNotifications({ message: translate('modules.createNewApp.steps.uploadImages.imagesUploadFailed'), type: 'error' }));
    }, [formState.dirtyFields, initialImageData, onSaveBinaryData, showNotifications, translate]);

    return (
        <FlexBox alignItems="center" flexDirection='column' height="calc(100% - 16px)" width="100%" justifyContent="space-between">
            <UploadImagesTab platformTypes={[PlatformTypes.Android, PlatformTypes.IOS]} headerLabel={headerLabel} menuColor={menuColor} />
            <TabFooterButtons onSubmitClickHandler={handleSubmit(onSubmitHandler)} mutationLoading={mutationLoading} />
        </FlexBox>
    );
});

export const UploadImages = React.memo((props: IUploadImagesProps) => {
    const { imageData } = props;
    const defaultValues = useUploadImageDefaultValues(imageData);
    const form = useForm<IUploadImagesFormFields>({
        mode: 'all',
        defaultValues
    });

    return (
        <FormProvider {...form}>
            <UploadImagesBase {...props} />
        </FormProvider>
    );
});
